import { useMutation } from '@tanstack/react-query';
import { Button, Icon } from '@tutero/ui';

import { resendVerificationEmail } from '@/lib/account';

import { reasonFor } from './useAccountWrite';

/**
 * The unverified address, named at the head of the sheet (`settings-is-a-dialog` D10), as Flutter
 * names it above its sections. Drawn off the reading the sheet just made, not off a
 * `currentUser` nothing reloaded (`settings_popup.dart:34`), so following the mail and
 * reopening the sheet clears it.
 *
 * A plain mutation, not `useAccountWrite`: a verification mail changes nothing about the
 * account, so there is nothing for the caches to settle.
 *
 * Flutter hides its 2FA section while an address is unverified
 * (`settings_popup.dart:107-110`). Not ported: the Worker has no such rule, and an
 * account that cannot be mailed is the one that most needs a second factor.
 */
export function EmailVerificationNotice({ email }: { email: string }) {
  const resend = useMutation({
    mutationFn: () => resendVerificationEmail(email),
  });

  return (
    <div
      role="alert"
      className="border-destructive/40 bg-destructive/5 flex flex-wrap items-center gap-2 rounded-md border p-3"
    >
      <Icon name="warning" size={16} className="text-destructive" />
      <span className="text-bold-s text-destructive">
        Email is not verified
      </span>
      {resend.isSuccess ? (
        <span role="status" className="text-regular-s ml-auto">
          We sent a link to {email}.
        </span>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="ml-auto"
          disabled={resend.isPending}
          onClick={() => resend.mutate()}
        >
          {resend.isPending ? 'Sending…' : 'Send Verification Email'}
        </Button>
      )}
      {resend.isError ? (
        <p className="text-destructive text-regular-s basis-full">
          {reasonFor(resend.error)}
        </p>
      ) : null}
    </div>
  );
}
